import { useState } from 'react'
import { Info } from 'lucide-react'
import {
  useMessagingAlerts,
  useMessagingHourly,
  useMessagingOverview,
  useMessagingRejections,
} from '@/hooks/useDeepDive'
import {
  sourceHeading,
  toDeliveryStats,
  toMessagingAlert,
  toRejections,
  toTrafficSeries,
} from '@/lib/messaging-data'
import { toFreshness } from '@/lib/deep-dive-adapters'
import type {
  MessagingChannel,
  MessagingProvider,
  MessagingWindow,
} from '@/lib/api/deep-dive.types'
import { ServiceAlertBanner } from './ServiceAlertBanner'
import { DeliveryStatTile } from './DeliveryStatTile'
import { SentVsRejectedChart } from './SentVsRejectedChart'
import { RejectionsTable } from './RejectionsTable'
import { PanelError } from './PanelState'

/** How many failures the table shows, and how much "Show more" adds. */
const PAGE = 25
const MAX_ROWS = 100

/**
 * Which channel each family serves, for wording only.
 *
 * The route family is the single selector on this screen, so the channel is
 * derived from it rather than passed alongside it — two props could disagree,
 * and a section headed "SMS" over `sendgrid/messaging/*` is exactly the mislabel
 * this rework removed. It is a fallback in any case: once a payload lands,
 * `source.channel` is what the section reads.
 */
const FAMILY_CHANNEL: Record<MessagingProvider, MessagingChannel> = {
  sendgrid: 'email',
  twilio: 'sms',
}

interface MessagingDeliverySectionProps {
  /** Which route family to read — `sendgrid` is email, `twilio` is SMS. */
  provider: MessagingProvider
  window: MessagingWindow
}

/**
 * One provider's delivery readout: heading, four tiles, the hourly chart, the
 * failures table and the blockage banner.
 *
 * Email and SMS are the same UI over the same four routes — `sendgrid/messaging/*`
 * and `twilio/messaging/*` answer identical shapes — so this is one component
 * used by both screens rather than two that drift apart. Which family it reads
 * is a prop; everything it *says* comes from the payload's `source` and
 * `message_type`, so a section cannot mislabel itself if a family is repointed.
 *
 * Each section owns its four queries, and the two families are keyed apart. That
 * is the point of the split: the vendors sync independently and fail
 * independently — SMS spent 2026-09-10 answering 503 on a Twilio auth error
 * while email was fine — and one dead vendor must never blank the other screen.
 *
 * The two are never combined. Nothing here adds a figure from one family to the
 * other: they are different providers measuring different things, and a summed
 * "deliverability" across SendGrid and Twilio would be a number with no meaning.
 */
export function MessagingDeliverySection({ provider, window }: MessagingDeliverySectionProps) {
  const [limit, setLimit] = useState(PAGE)
  const channel = FAMILY_CHANNEL[provider]

  const overview = useMessagingOverview(provider, window)
  const hourly = useMessagingHourly(provider, window)
  const failures = useMessagingRejections(provider, limit)
  const alerts = useMessagingAlerts(provider)

  const source = overview.data?.source ?? hourly.data?.source ?? failures.data?.source
  const heading = sourceHeading(source, overview.data?.message_type, { provider, channel })
  const alert = toMessagingAlert(alerts.data)
  const series = toTrafficSeries(hourly.data?.buckets)
  const notes = overview.data?.notes

  // "Failed" on SMS, "Bounced" on email — the chart and the table follow the
  // tile, so the three never call the same metric two different things.
  const isSms = (source?.channel ?? channel) === 'sms'
  const failureWord = isSms ? 'Failed' : 'Bounced'

  return (
    <section className="space-y-4">
      <div className="border-line flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-b pb-3">
        <h2 className="text-fg flex items-center gap-2 font-mono text-[13px] font-semibold tracking-[0.08em] uppercase">
          {heading}
          {notes?.length ? (
            // The notes are where the backend explains what it counted — that SMS
            // reads one From number only, that Twilio has no spam feed, that
            // email cannot tell prod from staging. Caveats worth having, not
            // worth the layout.
            <span title={notes.join('\n\n')} className="cursor-help">
              <Info
                aria-label="How these figures are counted"
                className="text-fg-subtle size-3.5"
              />
            </span>
          ) : null}
        </h2>
      </div>

      {alert && (
        <ServiceAlertBanner
          title={alert.title}
          actionLabel={alert.actionLabel}
          actionHref={alert.actionHref}
        />
      )}

      {overview.isError ? (
        <PanelError
          error={overview.error}
          onRetry={() => void overview.refetch()}
          className="border-line bg-surface rounded-lg border"
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {toDeliveryStats(overview.data, channel).map((stat) => (
            <DeliveryStatTile key={stat.id} stat={stat} />
          ))}
        </div>
      )}

      <SentVsRejectedChart
        series={series}
        title={`Sent vs ${failureWord} · ${window} by ${series.granularity} (UTC)`}
        secondaryLabel={failureWord}
        freshness={hourly.data && toFreshness(hourly.data)}
        isLoading={hourly.isLoading}
        error={hourly.error}
        onRetry={() => void hourly.refetch()}
      />

      <RejectionsTable
        title={isSms ? 'Recent delivery failures' : 'Recent bounces'}
        emptyLabel={
          isSms ? 'Nothing failed — no delivery failures to show.' : 'Nothing bounced — no failures to show.'
        }
        rejections={toRejections(failures.data)}
        freshness={failures.data && toFreshness(failures.data)}
        isLoading={failures.isLoading}
        error={failures.error}
        onRetry={() => void failures.refetch()}
        // The feed is cursor-paged; this panel walks it by asking for a bigger
        // page, which keeps every row on screen at once. It stops at MAX_ROWS —
        // past that the answer is a full log page, not a longer card.
        onShowMore={
          failures.data?.next_cursor && limit < MAX_ROWS
            ? () => setLimit((n) => Math.min(n + PAGE, MAX_ROWS))
            : undefined
        }
      />
    </section>
  )
}
